import { useCallback, useEffect, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { useAuth } from '@/core/providers/use-auth';

import type { PropsWithChildren } from 'react';

// ── Config ────────────────────────────────────────────────────────────────────

/** Milliseconds of inactivity before showing the warning modal. */
const IDLE_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes

/** Seconds the user has to respond before the session is locked. */
const LOCKOUT_COUNTDOWN_SECONDS = 20;

/** DOM events that count as user activity. */
const ACTIVITY_EVENTS: (keyof WindowEventMap)[] = [
  'mousemove',
  'mousedown',
  'keydown',
  'touchstart',
  'scroll',
  'click',
];

// ── Component ─────────────────────────────────────────────────────────────────

/**
 * Wraps the application and enforces a 30-minute inactivity policy.
 *
 * Flow:
 * 1. Any user interaction resets the 30-minute idle timer.
 * 2. When the timer expires, a modal appears over a blurred overlay asking
 *    "¿SIGUES AQUÍ?". The user has 20 seconds to click "REGRESAR".
 * 3. If no response within 20 seconds, the modal shows "SESIÓN BLOQUEADA
 *    POR INACTIVIDAD" and the only action is "VOLVER A INICIAR SESIÓN"
 *    which triggers Auth0 logout.
 * 4. Clicking "REGRESAR" resets the idle timer and closes the modal.
 */
export function InactivityProvider(props: PropsWithChildren) {
  const { logout, isAuthenticated } = useAuth();

  const [modal_state, setModalState] = useState<'hidden' | 'warning' | 'locked'>('hidden');
  const [countdown, setCountdown] = useState(LOCKOUT_COUNTDOWN_SECONDS);

  const idle_timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdown_interval = useRef<ReturnType<typeof setInterval> | null>(null);

  const clearIdleTimer = useCallback(() => {
    if (idle_timer.current) {
      clearTimeout(idle_timer.current);
      idle_timer.current = null;
    }
  }, []);

  const clearCountdownInterval = useCallback(() => {
    if (countdown_interval.current) {
      clearInterval(countdown_interval.current);
      countdown_interval.current = null;
    }
  }, []);

  const startIdleTimer = useCallback(() => {
    clearIdleTimer();
    idle_timer.current = setTimeout(() => {
      setModalState('warning');
      setCountdown(LOCKOUT_COUNTDOWN_SECONDS);

      countdown_interval.current = setInterval(() => {
        setCountdown((previous) => {
          if (previous <= 1) {
            clearCountdownInterval();
            setModalState('locked');
            return 0;
          }
          return previous - 1;
        });
      }, 1000);
    }, IDLE_TIMEOUT_MS);
  }, [clearIdleTimer, clearCountdownInterval]);

  const onResume = useCallback(() => {
    clearCountdownInterval();
    setModalState('hidden');
    setCountdown(LOCKOUT_COUNTDOWN_SECONDS);
    startIdleTimer();
  }, [clearCountdownInterval, startIdleTimer]);

  const onLogout = useCallback(() => {
    clearIdleTimer();
    clearCountdownInterval();
    logout();
  }, [clearIdleTimer, clearCountdownInterval, logout]);

  // Register / unregister activity listeners
  useEffect(() => {
    if (!isAuthenticated) return;

    startIdleTimer();

    const handleActivity = () => {
      // Only reset when the modal is not showing — once the warning appears
      // the user must explicitly click a button to interact with the app.
      if (modal_state === 'hidden') {
        startIdleTimer();
      }
    };

    ACTIVITY_EVENTS.forEach((event) => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    return () => {
      clearIdleTimer();
      clearCountdownInterval();
      ACTIVITY_EVENTS.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, modal_state]);

  return (
    <>
      {props.children}

      {modal_state !== 'hidden' && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center"
          aria-modal="true"
          role="dialog"
        >
          {/* Blurred overlay */}
          <div
            className="absolute inset-0 bg-base-300/70 backdrop-blur-sm"
            aria-hidden="true"
          />

          {/* Modal card */}
          <div className="relative z-10 flex w-full max-w-sm flex-col items-center gap-6 rounded-2xl bg-base-100 border border-base-300 px-8 py-10 shadow-2xl text-center">

            {modal_state === 'warning' && (
              <>
                {/* Countdown ring */}
                <div className="relative flex h-20 w-20 items-center justify-center">
                  <svg className="absolute inset-0 -rotate-90" viewBox="0 0 72 72">
                    <circle
                      cx="36" cy="36" r="32"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="4"
                      className="text-base-300"
                    />
                    <circle
                      cx="36" cy="36" r="32"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="4"
                      strokeLinecap="round"
                      strokeDasharray={`${2 * Math.PI * 32}`}
                      strokeDashoffset={`${2 * Math.PI * 32 * (1 - countdown / LOCKOUT_COUNTDOWN_SECONDS)}`}
                      className={twMerge(
                        'transition-all duration-1000',
                        countdown > 10 ? 'text-warning' : 'text-error',
                      )}
                    />
                  </svg>
                  <span className={twMerge(
                    'text-2xl font-bold tabular-nums',
                    countdown > 10 ? 'text-warning' : 'text-error',
                  )}>
                    {countdown}
                  </span>
                </div>

                <div className="flex flex-col gap-2">
                  <h2 className="text-2xl font-black tracking-tight">
                    ¿SIGUES AQUÍ?
                  </h2>
                  <p className="text-base-content/60 text-sm">
                    Tu sesión se cerrará por inactividad en{' '}
                    <span className="font-semibold text-base-content">{countdown}</span>{' '}
                    {countdown === 1 ? 'segundo' : 'segundos'}.
                  </p>
                </div>

                <button
                  className="btn btn-primary w-full"
                  onClick={onResume}
                >
                  REGRESAR
                </button>
              </>
            )}

            {modal_state === 'locked' && (
              <>
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-error/10 text-error">
                  {/* Lock icon inline to avoid extra import */}
                  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                  </svg>
                </div>

                <div className="flex flex-col gap-2">
                  <h2 className="text-xl font-black tracking-tight text-error">
                    SESIÓN BLOQUEADA<br />POR INACTIVIDAD
                  </h2>
                  <p className="text-base-content/60 text-sm">
                    Por seguridad tu sesión fue cerrada. Vuelve a iniciar sesión para continuar.
                  </p>
                </div>

                <button
                  className="btn btn-error w-full text-white"
                  onClick={onLogout}
                >
                  VOLVER A INICIAR SESIÓN
                </button>
              </>
            )}

          </div>
        </div>
      )}
    </>
  );
}
